/**
 * "Would you consider locking money away for a guaranteed income for life?" — asked once, before
 * any module's Suggestion Engine runs, in words a client can answer without knowing what an annuity
 * is (owner, 24-25 September 2026).
 *
 * WHY IT IS A GATE AND NOT A DISCLOSURE. The answer is an INPUT to the modelling: "no" means no
 * annuity candidate is generated at all, in any module (annuityPreference.ts). Asking after the
 * ranking would mean reshuffling the field on the answer, which is the failure the new-money
 * question was built to avoid — so this follows the same shape as NewMoneyProfileGate, and the two
 * sit side by side.
 *
 * WHERE IT APPEARS. In the modules with no Strategy Analysis Setup of their own: Support at Home,
 * the granny flat, the downsizer, and cashflow and Age Pension. The village and aged care ask the
 * same questions inside their setup pop-over instead, and write the same answer, so a client is
 * only ever asked once.
 *
 * The caller owns the gating condition — only where the run would actually offer an annuity, only
 * while unanswered — and the two exits: onAnswer saves to the client record, onSkip models both
 * versions without recording an answer, so the question is offered again next time.
 */
import React, { useState } from 'react';
import { Button } from '../ui/index.tsx';
import type { AnnuityAppetite, AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';

export interface AnnuityPreferenceGateProps {
    onAnswer: (appetite: AnnuityAppetite, residual: AnnuityResidual) => void;
    onSkip: () => void;
    /** One sentence naming what this module's strategies would free money up FROM (consumer voice). */
    contextLine?: string;
    /**
     * The guarantee window in this client's own years, from `guaranteeWindowWords`. This component
     * holds no fact-find, so the caller works it out; without it the sentence falls back to the
     * general rule rather than inventing a number.
     */
    guaranteeWindow?: string | null;
    /** True where the client is in, or entering, government-subsidised care — the only people the guaranteed shape is sold to. */
    offerGuaranteed?: boolean;
}

/*
 * A GRID, NOT A WRAPPING ROW (owner, 27 Sep 2026). Each button used to be as wide as its own label,
 * so answers to one question came out at different sizes and the wordiest looked like the
 * recommended one. The column count must be a literal class name: Tailwind builds from source text
 * and never emits an interpolated `grid-cols-${n}`.
 */
const CHOICE_COLUMNS: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-2',
};

const Choice = ({ options, onPick, ariaLabel }: {
    options: Array<{ id: string; label: string }>;
    onPick: (id: string) => void;
    ariaLabel: string;
}) => (
    <div className={`mt-3 grid grid-cols-1 ${CHOICE_COLUMNS[options.length] || 'sm:grid-cols-3'} gap-2`} role="group" aria-label={ariaLabel}>
        {options.map((o) => (
            <button
                key={o.id}
                type="button"
                onClick={() => onPick(o.id)}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:border-blue-500 hover:text-blue-700 transition-colors"
            >
                {o.label}
            </button>
        ))}
    </div>
);

const AnnuityPreferenceGate = ({ onAnswer, onSkip, contextLine, guaranteeWindow = null, offerGuaranteed = false }: AnnuityPreferenceGateProps) => {
    const [wantsAnnuity, setWantsAnnuity] = useState(false);

    return (
        <div className="mt-3 rounded-xl bg-white border border-slate-200/70 border-l-4 border-l-blue-500 p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">One question before I model your options</p>
            {!wantsAnnuity ? (
                <>
                    <p className="text-sm text-slate-700 mt-1.5">
                        {contextLine || 'Some of the options I’ll compare lock part of your money away with an insurer.'}{' '}
                        In return you get a <strong>guaranteed income for life</strong>. Centrelink counts only 60% of money
                        used that way, which can lift the Age Pension. Setting one up takes more than a form:{' '}
                        you get a quote from the insurer, directly or through an adviser, then apply.{' '}
                        <strong>Should I include these options?</strong>
                    </p>
                    <Choice
                        ariaLabel="Should I include a guaranteed income for life"
                        options={[
                            { id: 'none', label: 'No — keep it all accessible' },
                            { id: 'yes', label: 'Yes — include these options' },
                        ]}
                        onPick={(id) => {
                            if (id === 'yes') setWantsAnnuity(true);
                            else onAnswer(id as AnnuityAppetite, 'both');
                        }}
                    />
                </>
            ) : (
                <>
                    <p className="text-sm text-slate-700 mt-1.5">
                        <strong>Should any of it come back to the family on death?</strong> Guaranteeing more means a lower
                        income while you are alive. The full amount only comes back if death comes
                        early{guaranteeWindow ? `, and ${guaranteeWindow}` : ', within the first half of the period the rules set'}; after that it
                        falls away to nothing.
                    </p>
                    <Choice
                        ariaLabel="Should any of it come back to the family"
                        options={[
                            { id: 'returned', label: 'Yes — as much as the rules allow' },
                            ...(offerGuaranteed ? [{ id: 'guaranteed', label: 'Yes — the full amount, whenever I die' }] : []),
                            { id: 'nil', label: 'No — the highest income' },
                            { id: 'both', label: 'Show me whichever works best' },
                        ]}
                        onPick={(id) => onAnswer('yes', id as AnnuityResidual)}
                    />
                </>
            )}
            <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-slate-500">
                    Your answer is saved to the client profile, so you&apos;ll only be asked once &mdash; and you can change it any time.
                </p>
                <Button variant="secondary" size="sm" onClick={onSkip}>
                    Skip &mdash; show me both for now
                </Button>
            </div>
        </div>
    );
};

export default AnnuityPreferenceGate;
